import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ContentOverviewCard,
  Button,
  ButtonAppearance,
  ContentType,
  formatMinutes,
  ContentOverviewCardState,
} from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { N800 } from '@atlaskit/theme/colors';

import { getInspectModeData } from 'src/utils/contentful';
import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { User } from 'src/types';
import { LearningProgressStatus } from 'src/services/http/progress';
import { sendTrackEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { getCourseUrl, withPrefix } from 'src/utils/getUrl';
import { useCourseProgress } from 'src/store/progress/progressStore';
import { getDuration } from 'src/pages/LearningPathPage/utils';

interface CourseListProps {
  learningPath: ContentfulLearningPath | null;
  user?: User | null;
  numCoursesShown?: number;
  isLoading?: boolean;
}

export const MAX_COURSES_SHOWN = 6;

export const OldCourseList: React.FC<CourseListProps> = ({
  learningPath,
  user,
  numCoursesShown = MAX_COURSES_SHOWN,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldShowAllCourses, setShouldShowAllCourses] = useState<boolean>(false);
  const [{ progressForCourses, isLoadingProgressForCourses }] = useCourseProgress();

  const learningPathSlug = getSlug(learningPath);

  const onCardClick = useCallback(
    (course: ContentfulLearningPath['coursesCollection']['items'][0]): void => {
      const hasNotStartedLearningPath =
        !progressForCourses?.overallStatus || progressForCourses.overallStatus === LearningProgressStatus.NOT_STARTED;

      if (hasNotStartedLearningPath) {
        sendTrackEvent(
          'learningApp',
          'learningPath',
          'started',
          {
            learningPathSlug,
          },
          user?.account_id,
        );
      }

      if (course && learningPath) {
        const url = withPrefix(
          getCourseUrl(getSlug(course), learningPathSlug),
          getLearningPathsHubPart(location.pathname),
        );

        navigate(url);
      }
    },
    [navigate, learningPath, location.pathname, progressForCourses, user, learningPathSlug],
  );

  const handleHrefLink = useCallback(
    (course: ContentfulLearningPath['coursesCollection']['items'][0]): string => {
      if (!course || !learningPath) {
        return '';
      }

      const baseUrl = getCourseUrl(getSlug(course), learningPathSlug);
      const hubPart = getLearningPathsHubPart(location.pathname);
      const url = hubPart ? `${hubPart}${baseUrl}` : baseUrl;

      return withPrefix(url);
    },
    [learningPath, learningPathSlug, location.pathname],
  );

  const getCourseState = (courseId: string): ContentOverviewCardState => {
    if (isLoading || isLoadingProgressForCourses) {
      return ContentOverviewCardState.LOADING;
    }

    if (!progressForCourses || !user) {
      return ContentOverviewCardState.DEFAULT;
    }

    const courseProgress = progressForCourses.courses.find((p) => p.courseId === courseId);

    if (courseProgress?.status === LearningProgressStatus.IN_PROGRESS) {
      return ContentOverviewCardState.IN_PROGRESS;
    }

    if (courseProgress?.status === LearningProgressStatus.COMPLETED) {
      return ContentOverviewCardState.FINISHED;
    }

    return ContentOverviewCardState.DEFAULT;
  };

  const showAllCourses = useCallback(() => {
    setShouldShowAllCourses(true);
  }, [setShouldShowAllCourses]);

  if (!learningPath?.coursesCollection.items || learningPath?.coursesCollection.items.length === 0) {
    return null;
  }

  const filteredCourses = learningPath?.coursesCollection.items.filter(
    (course) => course?.lessonsCollection.items.length,
  );

  const coursesToShow = shouldShowAllCourses ? filteredCourses : filteredCourses?.slice(0, numCoursesShown);

  return (
    <section data-testid="course-list" css={sectionStyles}>
      <div css={containerStyles}>
        <p data-testid="course-quantity-title" css={headerStyles}>
          {filteredCourses?.length}
          {`${filteredCourses.length > 1 ? ' courses' : ' course'} in this learning path`}
        </p>
        {coursesToShow.map((course) => (
          <ContentOverviewCard
            inspectMode={getInspectModeData(course, ['title', 'overview'])}
            key={course?.sys.id}
            tags={[{ label: formatMinutes(getDuration(course)) }]}
            heading={course?.title}
            subheading={course?.teaser}
            collapsedSectionTitle="Lessons"
            collapsedSectionItems={course?.lessonsCollection.items
              .filter((section) => section.title)
              .map((section) => section.title)}
            onClickButton={() => onCardClick(course)}
            state={getCourseState(course?.sys.id)}
            contentType={ContentType.LEARNING_PATH}
            href={handleHrefLink(course)}
          />
        ))}
      </div>
      <div css={css({ marginTop: '1rem' })}>
        {coursesToShow.length < filteredCourses.length && (
          <Button label="See All Courses" appearance={ButtonAppearance.LINK} onClick={showAllCourses} />
        )}
      </div>
    </section>
  );
};

const sectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  rowGap: '16px',
  marginTop: '24px',
  width: '100%',
});

const containerStyles = css({
  width: '100%',
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
});

const headerStyles = css({
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '24px',
  lineHeight: '32px',
  fontWeight: 600,
  color: N800,
  marginTop: 0,
  marginBottom: '16px',
});
